import Images from "assets/images"
import {
    REVIEW,
    ASC,
} from "const"
import {
    CcLibraryItemList,
    CcLibrarySet,
    CcReviewReviewItem,
    MtProject,
} from "server/models"
import {
    ModuleList,
    UserList,
    UserSettings,
    UserData,
    LangType,
    CcLibrarySetList,
    ReviewComplianceRevisionVersionActiveList,
    ReviewComplianceVersionStateList,
    ReviewComplianceVersionHistoryList,
    ReviewComplianceVersionHistoryNestedList,
    ReviewComplianceHistoryNestedList,
    ReviewReviewRevisionActiveList,
    ReviewReviewStateList,
    ReviewReviewHistoryList,
    ReviewReviewHistoryNestedList,

    GetFile,

    LoadCcApprovalLevels,
    LoadCcResponses,
    LoadAllCcLibrarySets,
    LoadCcReviewReviewTypes,
    LoadCcReviewDocuments,
    LoadNestedItems,
    LoadCcReviewComplianceRevisions,
    LoadAllCcReviewComplianceItemState,
    LoadAllCcReviewComplianceHistory,
    LoadCcReviewReviewItems,
    LoadCcReviewReviewRevisions,
    LoadCcReviewReviewItemState,
    LoadCcReviewReviewHistory,
    SortCcLibrarySets,
    CcApprovalLevelList,
} from "client/store"
import {
    PrepareNestedHistory,
    TableGroupedIds,
} from "client/components"

import pdfmake from "client/components/pdfmake/pdfmake"
import vfs from "client/components/pdfmake/vfs_fonts"
import fontList from "client/components/pdfmake/fontList"

import {
    ReportAccountLogo,
    ReportProjectLogo,
} from "client/components/pdfmake/template/functions"
import * as Const from "client/components/pdfmake/template/const"
import templateStyles from "client/components/pdfmake/template/styles"

import { Header, Footer } from "./headerFooter"
import TitlePage from "./titlePage"
import CopyrightPage from "./copyrightPage"
import OverviewPage from "./overviewPage"
import ReviewComplianceSetsPage from "./reviewComplianceSetsPage"
import ReviewPage from "./reviewPage"


pdfmake.addVirtualFileSystem(vfs)
pdfmake.addFonts(fontList)


const ReviewComplianceExportToPDF = (
    reviewComplianceRevisionActiveList: ReviewComplianceRevisionVersionActiveList | null,
    reviewReviewRevisionActiveList: ReviewReviewRevisionActiveList | null,
    project: MtProject,
    moduleList: ModuleList,
    userList: UserList,
    userSettings: UserSettings,
    userData: UserData,
    lang: LangType,
    callback: (isLoading: boolean) => void,
) => {
    callback(true)

    Promise.all([
        // formal approval levels
        LoadCcApprovalLevels(userData.accountId, project.id, 0)
        .then((res) => res),
        // review approval levels
        LoadCcApprovalLevels(userData.accountId, project.id, 2)
        .then((res) => res),


        // responsible responses
        LoadCcResponses(userData.accountId, project.id, 0)
        .then((res) => res.responseList),
        // review (compliance) responses
        LoadCcResponses(userData.accountId, project.id, 2)
        .then((res) => res.responseList),
        // review (review) responses
        LoadCcResponses(userData.accountId, project.id, 3)
        .then((res) => res.responseList),


        // review types
        LoadCcReviewReviewTypes(userData.accountId, project.id)
        .then((res) => res.typeList),


        LoadCcReviewDocuments(project.id)
        .then((reviewDocuments) => {
            // review (review) data
            const reviewData: { [fileId: number]: CcReviewReviewItem[] } = {}
            const reviewReviewRevisionList: ReviewReviewRevisionActiveList = {}
            const reviewStateList: ReviewReviewStateList = {}
            const reviewHistoryList: ReviewReviewHistoryList = {}
            const reviewHistoryNestedList: ReviewReviewHistoryNestedList = {}
            const reviewPromises: Promise<any>[] = []

            reviewDocuments.forEach((file) => {
                reviewPromises.push(
                    LoadCcReviewReviewItems(project.id, file.id).then((resItems) => {
                        reviewData[file.id] = resItems.items
                        const promiseArray: Promise<any>[] = []

                        resItems.items.forEach((item) => {
                            promiseArray.push(
                                LoadCcReviewReviewRevisions(item.id).then((revisions) => {
                                    let revision = revisions[revisions.length - 1] // by default use the last revision
                                    if (reviewReviewRevisionActiveList && reviewReviewRevisionActiveList[item.id]) {
                                        revision = reviewReviewRevisionActiveList[item.id]
                                    }

                                    reviewReviewRevisionList[item.id] = revision

                                    return Promise.all([
                                        LoadCcReviewReviewItemState(item.id, revision.id).then((res) => {
                                            reviewStateList[item.id] = res
                                        }),
                                        LoadCcReviewReviewHistory(item.id, revision.id).then((res) => {
                                            reviewHistoryList[item.id] = res
                                            reviewHistoryNestedList[item.id] = PrepareNestedHistory(res)
                                        }),
                                    ])
                                }),
                            )
                        })

                        return Promise.all(promiseArray)
                    }),
                )
            })


            return Promise.all([
                // review (compliance) data
                LoadAllCcLibrarySets(userData.accountId, project.id)
                .then((setsResponse) => {
                    const setList: CcLibrarySetList = {}
                    let itemList: CcLibraryItemList = {}
                    let nested: { [setId: number]: TableGroupedIds } = {}

                    const reviewComplianceRevisionList: ReviewComplianceRevisionVersionActiveList = {}
                    let reviewComplianceStateList: ReviewComplianceVersionStateList = {}
                    let reviewComplianceHistoryList: ReviewComplianceVersionHistoryList = {}
                    const reviewComplianceHistoryNestedList: ReviewComplianceVersionHistoryNestedList = {}
                    const promiseArray: Promise<any>[] = []

                    Object.values(setsResponse).forEach((set: CcLibrarySet) => {
                        set.modules.forEach((row) => {
                            if (row.module === REVIEW) {
                                setList[set.id] = set

                                promiseArray.push(
                                    LoadNestedItems(set.id).then((res) => {
                                        itemList = { ...itemList, ...res.itemList }
                                        nested = {
                                            ...nested,
                                            [set.id]: res.nested,
                                        }
                                    }),
                                )

                                reviewDocuments.forEach((document) => {
                                    document.versions.forEach((version) => {
                                        promiseArray.push(
                                            LoadCcReviewComplianceRevisions(project.id, version.fileId, version.id, set.id)
                                            .then((revisions) => {
                                                // if file version was never selected in the list - there will no revisions
                                                if (!revisions.length) return null

                                                let revision = revisions[revisions.length - 1] // by default use the last revision
                                                if (
                                                    reviewComplianceRevisionActiveList
                                                    && reviewComplianceRevisionActiveList[version.id]
                                                    && reviewComplianceRevisionActiveList[version.id][set.id]
                                                ) revision = reviewComplianceRevisionActiveList[version.id][set.id]

                                                if (!reviewComplianceRevisionList[version.id]) reviewComplianceRevisionList[version.id] = {}
                                                reviewComplianceRevisionList[version.id][set.id] = revision

                                                return Promise.all([
                                                    LoadAllCcReviewComplianceItemState(revision.id).then((res) => {
                                                        reviewComplianceStateList = {
                                                            ...reviewComplianceStateList,
                                                            [version.id]: {
                                                                ...reviewComplianceStateList[version.id],
                                                                ...res,
                                                            },
                                                        }
                                                    }),
                                                    LoadAllCcReviewComplianceHistory(revision.id).then((res) => {
                                                        reviewComplianceHistoryList = {
                                                            ...reviewComplianceHistoryList,
                                                            [version.id]: {
                                                                ...reviewComplianceHistoryList[version.id],
                                                                ...res,
                                                            },
                                                        }
                                                        const nestedHistory: ReviewComplianceHistoryNestedList = {}
                                                        Object.keys(res).forEach((index) => {
                                                            const itemId = Number(index)
                                                            nestedHistory[itemId] = PrepareNestedHistory(res[itemId])
                                                        })
                                                        reviewComplianceHistoryNestedList[version.id] = {
                                                            ...reviewComplianceHistoryNestedList[version.id],
                                                            ...nestedHistory,
                                                        }
                                                    }),
                                                ])
                                            }),
                                        )
                                    })
                                })
                            }
                        })
                    })

                    const sortedIds = SortCcLibrarySets(Object.values(setList), "name", ASC, {})

                    return Promise.all(promiseArray).then(() => ({
                        setList,
                        setIds: sortedIds,
                        itemList,
                        nested,
                        reviewDocuments,
                        reviewComplianceRevisionList,
                        reviewComplianceStateList,
                        reviewComplianceHistoryList,
                        reviewComplianceHistoryNestedList,
                    }))
                }),
                Promise.all(reviewPromises).then(() => ({
                    reviewData,
                    reviewReviewRevisionList,
                    reviewStateList,
                    reviewHistoryList,
                    reviewHistoryNestedList,
                })),
            ])
        }),
    ]).then(([
        formalLevelList,
        reviewLevelList,

        responsibleResponses,
        reviewComplianceResponses,
        reviewReviewResponses,

        reviewTypes,
        [
            setData,
            {
                reviewData,
                reviewReviewRevisionList,
                reviewStateList,
                reviewHistoryList,
                reviewHistoryNestedList,
            },
        ],
    ]) => {
        let accountLogo: string
        let projectLogo: string

        // load all used images as base64 before creating pdf
        const promiseArray: Promise<any>[] = [
            ReportAccountLogo(userData).then((res) => {
                accountLogo = res
            }),
            ReportProjectLogo(project, userSettings).then((res) => {
                projectLogo = res
            }),
        ]

        // load compliance set logos as base64
        const setImageBase64: { [setId: number]: string } = {}
        const defaultSetLogo = `${window.location.origin}${Images.complianceSetLogo}`

        setData.setIds.forEach((setId) => {
            const set = setData.setList[setId]
            promiseArray.push(
                GetFile(set.logo, defaultSetLogo).then((res) => {
                    setImageBase64[set.id] = res.data
                }),
            )
        })


        Promise.all(promiseArray).then(() => {
            const levelList: CcApprovalLevelList = {
                ...formalLevelList.levelList,
                ...reviewLevelList.levelList,
            }
            const responseList = {
                ...responsibleResponses,
                ...reviewComplianceResponses,
                ...reviewReviewResponses,
            }

            const docDefinition = {
                info: {
                    title: lang.ccReviewComplianceReport,
                    author: lang.risknowlogy,
                    subject: lang.result,
                    keywords: "CC, PDF, Review Compliance, Risknowlogy",
                },
                pageMargins: Const.PAGE_MARGINS,
                pageSize: "A4",
                images: {
                    accountLogo,
                    projectLogo,
                    downwardArrow: `${window.location.origin}${Images.downwardArrow}`,
                },
                defaultStyle: {
                    fontSize: 11,
                    font: "Arial",
                },
                styles: templateStyles,
                header: (currentPage: number) => Header(currentPage, project),
                footer: (currentPage: number, pageCount: number) => (
                    Footer(currentPage, pageCount, project, userData, lang)
                ),
                content: TitlePage(project, lang).concat(
                    CopyrightPage(moduleList, lang),
                    [{
                        pageBreak: "before",
                        toc: {
                            title: {
                                text: lang.tableOfContents,
                                style: Const.HEADER,
                                tocItem: true,
                                tocStyle: Const.TOC_HEADER,
                                tocMargin: [0, 5, 0, 0],
                            },
                            numberStyle: {
                                bold: true,
                                fontSize: 11,
                            },
                        },
                    }],
                    OverviewPage(setData.setList, setData.setIds, project, lang),
                    ReviewComplianceSetsPage(
                        setData.setList,
                        setData.setIds,
                        setData.itemList,
                        setData.nested,

                        setData.reviewDocuments,
                        setData.reviewComplianceRevisionList,
                        setData.reviewComplianceStateList,
                        setData.reviewComplianceHistoryList,
                        setData.reviewComplianceHistoryNestedList,

                        setImageBase64,

                        levelList,
                        formalLevelList.levelIds,
                        reviewLevelList.levelIds,
                        responseList,
                        userList,
                        lang,
                    ),
                    ReviewPage(
                        reviewData,
                        reviewReviewRevisionList,
                        reviewStateList,
                        reviewHistoryList,
                        reviewHistoryNestedList,
                        reviewTypes,
                        setData.reviewDocuments,
                        levelList,
                        reviewLevelList.levelIds,
                        responseList,
                        userList,
                        lang,
                    ),
                ),
            }

            callback(false)
            pdfmake.createPdf(docDefinition).open()
        })
    })
}
export default ReviewComplianceExportToPDF
